import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { AnalyticsService } from '../../services/gtag.service';
import { DisplayService } from '../../services/display.service';
import { User } from '../../entities/user.model';

@Component({
  selector: 'app-setup',
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, TranslateModule],
  templateUrl: './setup.page.html',
  styleUrls: ['./setup.page.scss'],
})
export class SetupPage implements OnInit {
  textSize: 1 | 2 | 3 | 4 | 5 = 3; // Default to medium size
  darkMode: boolean = false; // Default to light mode
  private currentUser: User | null = null;
  private nativeLanguage = '';
  private targetLanguage = '';
  private selectedLevel = 1;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private authService: AuthService,
    private userService: UserService,
    private analyticsService: AnalyticsService,
    private displayService: DisplayService
  ) {
    // Get the passed state from navigation
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.currentUser = navigation.extras.state['user'];

      // Handle language data (may be passed with or without user)
      const languageData = navigation.extras.state['languageData'];
      if (languageData) {
        // Store language data as component properties for validation
        this.nativeLanguage = languageData.nativeLanguage;
        this.targetLanguage = languageData.targetLanguage;
        this.selectedLevel = languageData.selectedLevel;

        // If user exists, apply language settings
        if (this.currentUser) {
          this.currentUser.nativeLanguage = languageData.nativeLanguage;
          this.currentUser.targetLanguage = languageData.targetLanguage;
          this.currentUser.level = { [languageData.targetLanguage]: languageData.selectedLevel };
        }
      }
    }
  }

  ngOnInit() {
    document.title = 'LingDisco';

    const nativeFromQuery = this.route.snapshot.queryParamMap.get('native');
    const targetFromQuery = this.route.snapshot.queryParamMap.get('target');
    const levelFromQuery = this.route.snapshot.queryParamMap.get('level');

    if (nativeFromQuery) {
      this.nativeLanguage = nativeFromQuery;
    }
    if (targetFromQuery) {
      this.targetLanguage = targetFromQuery;
    }
    if (levelFromQuery) {
      const parsedLevel = Number(levelFromQuery);
      if ([1, 2, 3].includes(parsedLevel)) {
        this.selectedLevel = parsedLevel;
      }
    }

    // Use the user data passed from level page, or get current user
    this.currentUser = this.currentUser || this.authService.currentUserValue;

    if (this.currentUser) {
      // Load existing preferences if available
      this.textSize = this.currentUser.textSize || 3;
      this.darkMode = this.currentUser.darkMode || false;

      // Keep setup flow aligned with language chosen on beginning/level screens.
      if (this.nativeLanguage) {
        this.currentUser.nativeLanguage = this.nativeLanguage;
      }
      if (this.targetLanguage) {
        this.currentUser.targetLanguage = this.targetLanguage;
        this.currentUser.level = { ...this.currentUser.level, [this.targetLanguage]: this.selectedLevel };
      }

      // Set the app language to user's native language
      const languageToUse = this.nativeLanguage || this.currentUser.nativeLanguage;
      if (languageToUse) {
        this.translate.use(languageToUse);
      }
    } else if (this.nativeLanguage) {
      // We have language data but no user - this is fine, we'll create a user later
      this.textSize = 3; // Default
      this.darkMode = false; // Default
      this.translate.use(this.nativeLanguage);
    } else {
      // Redirect to beginning if no user and no language data
      this.router.navigate(['/beginning']);
      return;
    }

    // Apply initial theme
    this.applyTheme();
  }

  private applyTheme() {
    // Apply theme changes immediately for preview
    this.displayService.setDarkMode(this.darkMode);
    this.displayService.setTextSize(this.textSize);
  }

  // Called when text size changes
  onTextSizeChange() {
    this.displayService.setTextSize(this.textSize);
  }

  // Called when theme changes
  onThemeChange() {
    this.displayService.setDarkMode(this.darkMode);
  }

  // Get button text based on user tour completion status
  getButtonText(): string {
    if (this.currentUser?.completedTour) {
      return this.translate.instant('SETUP.APPLY');
    }
    return this.translate.instant('SETUP.START_LEARNING');
  }

  // Check if user has completed tour
  hasCompletedTour(): boolean {
    return this.currentUser?.completedTour || false;
  }

  async savePreferences() {
    try {
      const effectiveNative = this.nativeLanguage || this.currentUser?.nativeLanguage;
      const effectiveTarget = this.targetLanguage || this.currentUser?.targetLanguage;

      if (!effectiveNative || !effectiveTarget) {
        console.error('Missing required user fields');
        return;
      }

      if (this.currentUser) {
        // Signed-in user: persist to Firestore.
        this.currentUser.textSize = this.textSize;
        this.currentUser.darkMode = this.darkMode;
        this.currentUser.nativeLanguage = effectiveNative;
        this.currentUser.targetLanguage = effectiveTarget;
        this.currentUser.level = { ...this.currentUser.level, [effectiveTarget]: this.selectedLevel };
      } else {
        // Guest user: keep settings local only.
        const guestPreferences = {
          nativeLanguage: effectiveNative,
          targetLanguage: effectiveTarget,
          level: this.selectedLevel,
          textSize: this.textSize,
          darkMode: this.darkMode
        };
        localStorage.setItem('guestPreferences', JSON.stringify(guestPreferences));
      }

      // Apply theme immediately
      this.applyTheme();

      // Apply localization immediately
      this.translate.use(effectiveNative);

      // Save changes to Firestore only for signed-in users
      if (this.currentUser) {
        await this.userService.applyChanges(this.currentUser);
      }

      // Track analytics
      await this.analyticsService.trackEvent('preferences_saved', 'setup', 'user_preferences');

      // Always return to playlists from setup.
      this.router.navigate(['/playlists']);
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  }

  goBack() {
    // If user has completed tour, go back to settings
    if (this.currentUser?.completedTour) {
      this.router.navigate(['/settings']);
      return;
    }

    // Otherwise, handle normal flow
    const native = this.currentUser?.nativeLanguage || this.nativeLanguage;
    const target = this.currentUser?.targetLanguage || this.targetLanguage;

    if (native && target) {
      this.router.navigate(['/level'], {
        queryParams: {
          native: native,
          target: target
        }
      });
    } else {
      this.router.navigate(['/beginning']);
    }
  }
}
